import { useState, useEffect, useMemo } from 'react'
import { differenceInMinutes } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'
import { Trash2, Globe, ArrowRight, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAgencyTheme } from '@/contexts/AgencyThemeContext'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { TimeEntry, Client, CreateTimeEntryRequest, UpdateTimeEntryRequest } from '@/types'
import { Check, ChevronsUpDown } from 'lucide-react'

// Helper function to get GMT offset for a timezone
function getGMTOffset(timezone: string): string {
  try {
    const now = new Date()
    const offset = formatInTimeZone(now, timezone, 'xxx') // Returns format like +05:30 or -08:00
    const hours = parseInt(offset.slice(0, 3), 10)
    const minutes = offset.slice(4, 6)

    if (hours === 0 && minutes === '00') {
      return 'GMT'
    }

    const sign = hours >= 0 ? '+' : ''
    if (minutes === '00') {
      return `GMT${sign}${hours}`
    }
    return `GMT${sign}${hours}:${minutes}`
  } catch {
    return ''
  }
}

// Common timezones for the selector
const TIMEZONES = [
  { value: 'UTC', label: 'UTC', region: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (ET)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (CT)', region: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', region: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas' },
  { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow', region: 'Europe' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India (IST)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'Asia' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', region: 'Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)', region: 'Pacific' },
]

interface TimeEntryModalProps {
  isOpen: boolean
  onClose: () => void
  entry: TimeEntry | null
  selectedSlot: { start: Date; end: Date } | null
  clients: Client[]
  onSave: (data: CreateTimeEntryRequest | UpdateTimeEntryRequest) => void
  onDelete: () => void
  isLoading?: boolean
}

export default function TimeEntryModal({
  isOpen,
  onClose,
  entry,
  selectedSlot,
  clients,
  onSave,
  onDelete,
  isLoading,
}: TimeEntryModalProps) {
  const { t } = useTranslation()
  const { timezone: agencyTimezone } = useAgencyTheme()
  const [description, setDescription] = useState('')
  const [clientId, setClientId] = useState<string>('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [isBillable, setIsBillable] = useState(true)
  const [hourlyRate, setHourlyRate] = useState<string>('')
  const [selectedTimezone, setSelectedTimezone] = useState(agencyTimezone)
  const [timezoneOpen, setTimezoneOpen] = useState(false)
  const [timezoneSearchQuery, setTimezoneSearchQuery] = useState('')

  // Initialize form when modal opens
  // Times are displayed in the selected timezone (defaults to agency timezone)
  useEffect(() => {
    if (entry) {
      setDescription(entry.description)
      setClientId(entry.client_id || '')
      setSelectedTimezone(agencyTimezone) // Reset to agency timezone when editing
      // Convert UTC times to agency timezone for display
      setStartTime(formatInTimeZone(new Date(entry.start_time), agencyTimezone, "yyyy-MM-dd'T'HH:mm"))
      setEndTime(entry.end_time ? formatInTimeZone(new Date(entry.end_time), agencyTimezone, "yyyy-MM-dd'T'HH:mm") : '')
      setIsBillable(entry.is_billable)
      setHourlyRate(entry.hourly_rate?.toString() || '')
    } else if (selectedSlot) {
      setDescription('')
      setClientId('')
      setSelectedTimezone(agencyTimezone) // Reset to agency timezone for new entries
      // selectedSlot dates are already in UTC (converted by TimeCalendarView)
      // Convert to agency timezone for display
      setStartTime(formatInTimeZone(selectedSlot.start, agencyTimezone, "yyyy-MM-dd'T'HH:mm"))
      setEndTime(formatInTimeZone(selectedSlot.end, agencyTimezone, "yyyy-MM-dd'T'HH:mm"))
      setIsBillable(true)
      setHourlyRate('')
    }
  }, [entry, selectedSlot, agencyTimezone])

  // Get the selected timezone label and GMT offset
  const selectedTimezoneLabel = useMemo(() => {
    const tz = TIMEZONES.find((t) => t.value === selectedTimezone)
    return tz ? tz.label : selectedTimezone
  }, [selectedTimezone])

  const selectedGMTOffset = useMemo(() => getGMTOffset(selectedTimezone), [selectedTimezone])

  // Filter timezones based on search query
  const filteredTimezones = useMemo(() => {
    if (!timezoneSearchQuery.trim()) return TIMEZONES
    const query = timezoneSearchQuery.toLowerCase()
    return TIMEZONES.filter(
      (tz) =>
        tz.value.toLowerCase().includes(query) ||
        tz.label.toLowerCase().includes(query) ||
        tz.region.toLowerCase().includes(query)
    )
  }, [timezoneSearchQuery])

  // Calculate the times in agency timezone for preview (when using a different timezone)
  const agencyTimePreview = useMemo(() => {
    if (!startTime || !endTime || selectedTimezone === agencyTimezone) return null

    try {
      // Parse the entered times as if they're in the selected timezone
      const startDateInSelectedTz = new Date(startTime)
      const endDateInSelectedTz = new Date(endTime)

      // Convert from selected timezone to UTC
      const startUTC = fromZonedTime(startDateInSelectedTz, selectedTimezone)
      const endUTC = fromZonedTime(endDateInSelectedTz, selectedTimezone)

      // Format in agency timezone
      const startInAgency = formatInTimeZone(startUTC, agencyTimezone, 'MMM d, h:mm a')
      const endInAgency = formatInTimeZone(endUTC, agencyTimezone, 'h:mm a')
      const startTzAbbr = formatInTimeZone(startUTC, selectedTimezone, 'zzz')
      const agencyTzAbbr = formatInTimeZone(startUTC, agencyTimezone, 'zzz')

      return {
        start: startInAgency,
        end: endInAgency,
        startTzAbbr,
        agencyTzAbbr,
      }
    } catch {
      return null
    }
  }, [startTime, endTime, selectedTimezone, agencyTimezone])

  // Check if end time is before start time
  const timeValidationError = useMemo(() => {
    if (!startTime || !endTime) return null
    const start = new Date(startTime)
    const end = new Date(endTime)
    if (end <= start) {
      return t('timeTracking.endTimeBeforeStartError', 'End time must be after start time')
    }
    return null
  }, [startTime, endTime, t])

  // Calculate duration
  const calculateDuration = () => {
    if (!startTime || !endTime || timeValidationError) return null
    const start = new Date(startTime)
    const end = new Date(endTime)
    const minutes = differenceInMinutes(end, start)
    if (minutes < 0) return null
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}h ${mins}m`
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!description.trim() || !startTime || !endTime) return

    // Prevent submission if there's a time validation error
    if (timeValidationError) return

    // The form times are in the selected timezone, convert to UTC for storage
    const startDateInSelectedTz = new Date(startTime)
    const endDateInSelectedTz = new Date(endTime)

    // Convert from selected timezone to UTC
    const startDateUTC = fromZonedTime(startDateInSelectedTz, selectedTimezone)
    const endDateUTC = fromZonedTime(endDateInSelectedTz, selectedTimezone)

    const data: CreateTimeEntryRequest | UpdateTimeEntryRequest = {
      description: description.trim(),
      client_id: clientId || undefined,
      start_time: startDateUTC.toISOString(),
      end_time: endDateUTC.toISOString(),
      is_billable: isBillable,
      hourly_rate: hourlyRate ? parseFloat(hourlyRate) : undefined,
    }

    onSave(data)
  }

  const isEditing = !!entry

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? t('timeTracking.editEntry') : t('timeTracking.newEntry')}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? t('timeTracking.editEntryDescription')
                : t('timeTracking.newEntryDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">{t('timeTracking.description')} *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('timeTracking.descriptionPlaceholder')}
                required
              />
            </div>

            {/* Client */}
            <div className="grid gap-2">
              <Label htmlFor="client">{t('timeTracking.client')}</Label>
              <Select value={clientId || 'none'} onValueChange={(val) => setClientId(val === 'none' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('timeTracking.selectClient')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('timeTracking.noClient')}</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Timezone selector */}
            <div className="grid gap-2">
              <Label>{t('timeTracking.timezone', 'Timezone')}</Label>
              <Popover open={timezoneOpen} onOpenChange={(isOpen) => {
                setTimezoneOpen(isOpen)
                if (!isOpen) setTimezoneSearchQuery('')
              }}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={timezoneOpen}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      {selectedTimezoneLabel}
                      <span className="text-xs text-muted-foreground">({selectedGMTOffset})</span>
                      {selectedTimezone === agencyTimezone && (
                        <span className="text-xs text-muted-foreground">(Agency Default)</span>
                      )}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  {/* Search input */}
                  <div className="flex items-center border-b px-3">
                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                    <Input
                      placeholder={t('timeTracking.searchTimezone', 'Search timezone...')}
                      value={timezoneSearchQuery}
                      onChange={(e) => setTimezoneSearchQuery(e.target.value)}
                      className="h-11 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>

                  {/* Scrollable list */}
                  <div
                    className="max-h-[300px] overflow-y-auto"
                    style={{
                      overflowY: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      touchAction: 'pan-y',
                    }}
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {filteredTimezones.length === 0 ? (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        {t('timeTracking.noTimezoneFound', 'No timezone found.')}
                      </p>
                    ) : (
                      <>
                        {/* Agency default option at top (only if it matches search or no search) */}
                        {(!timezoneSearchQuery.trim() ||
                          agencyTimezone.toLowerCase().includes(timezoneSearchQuery.toLowerCase()) ||
                          'agency default'.includes(timezoneSearchQuery.toLowerCase())) && (
                          <div className="p-1">
                            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                              Default
                            </div>
                            <div
                              onClick={() => {
                                setSelectedTimezone(agencyTimezone)
                                setTimezoneOpen(false)
                                setTimezoneSearchQuery('')
                              }}
                              className={cn(
                                'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                                'hover:bg-accent hover:text-accent-foreground',
                                selectedTimezone === agencyTimezone && 'bg-accent'
                              )}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedTimezone === agencyTimezone ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              <Globe className="mr-2 h-4 w-4" />
                              <span className="flex-1">
                                Agency Default ({TIMEZONES.find(tz => tz.value === agencyTimezone)?.label || agencyTimezone})
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {getGMTOffset(agencyTimezone)}
                              </span>
                            </div>
                          </div>
                        )}
                        {/* Group other timezones by region */}
                        {['UTC', 'Americas', 'Europe', 'Asia', 'Pacific'].map((region) => {
                          const regionTimezones = filteredTimezones.filter(
                            (tz) => tz.region === region && tz.value !== agencyTimezone
                          )
                          if (regionTimezones.length === 0) return null
                          return (
                            <div key={region} className="p-1">
                              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                                {region}
                              </div>
                              {regionTimezones.map((tz) => (
                                <div
                                  key={tz.value}
                                  onClick={() => {
                                    setSelectedTimezone(tz.value)
                                    setTimezoneOpen(false)
                                    setTimezoneSearchQuery('')
                                  }}
                                  className={cn(
                                    'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                                    'hover:bg-accent hover:text-accent-foreground',
                                    selectedTimezone === tz.value && 'bg-accent'
                                  )}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      selectedTimezone === tz.value ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                  <span className="flex-1">{tz.label}</span>
                                  <span className="text-xs text-muted-foreground ml-2">
                                    {getGMTOffset(tz.value)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )
                        })}
                      </>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Time inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="startTime">{t('timeTracking.startTime')} *</Label>
                <Input
                  id="startTime"
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="endTime" className={timeValidationError ? 'text-destructive' : ''}>
                  {t('timeTracking.endTime')} *
                </Label>
                <Input
                  id="endTime"
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  className={timeValidationError ? 'border-destructive focus-visible:ring-destructive' : ''}
                />
              </div>
            </div>

            {/* Time validation error */}
            {timeValidationError && (
              <div className="text-sm text-destructive font-medium">
                {timeValidationError}
              </div>
            )}

            {/* Agency timezone preview (when using different timezone) */}
            {agencyTimePreview && (
              <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-3">
                <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                  <Globe className="h-4 w-4" />
                  <span className="font-medium">{t('timeTracking.inAgencyTimezone', 'In Agency Timezone')}:</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {agencyTimePreview.start} - {agencyTimePreview.end}
                  </span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                    {agencyTimePreview.agencyTzAbbr}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <span>{agencyTimePreview.startTzAbbr}</span>
                  <ArrowRight className="h-3 w-3" />
                  <span>{agencyTimePreview.agencyTzAbbr}</span>
                </div>
              </div>
            )}

            {/* Duration display */}
            {calculateDuration() && (
              <div className="text-sm text-muted-foreground">
                {t('timeTracking.duration')}: <span className="font-medium">{calculateDuration()}</span>
              </div>
            )}

            {/* Billable toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="billable">{t('timeTracking.billable')}</Label>
              <Switch
                id="billable"
                checked={isBillable}
                onCheckedChange={setIsBillable}
              />
            </div>

            {/* Hourly rate */}
            {isBillable && (
              <div className="grid gap-2">
                <Label htmlFor="hourlyRate">{t('timeTracking.hourlyRate')}</Label>
                <Input
                  id="hourlyRate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between">
            {isEditing && (
              <Button
                type="button"
                variant="destructive"
                onClick={onDelete}
                disabled={isLoading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('common.delete')}
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isLoading || !!timeValidationError}>
                {isLoading ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
